import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@material-ui/core";
import {
  Add as AddIcon,
  FormatListBulleted as ListIcon,
  DeleteOutline as DeleteIcon,
  Description as NoteIcon
} from "@material-ui/icons";
import api from "../../services/api";

const NotepadWidget = () => {
  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [title, setTitle] = useState("Bloco de Notas");
  const [noteText, setNoteText] = useState("");
  const [savingStatus, setSavingStatus] = useState("Salvo");
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const isFirstRender = useRef(true);

  // Fetch initial list of notes
  const fetchNotes = async (preferredId = null) => {
    try {
      const response = await api.get("/user-notes");
      const list = response.data || [];
      setNotes(list);

      if (list.length > 0) {
        const savedActiveId = preferredId || Number(localStorage.getItem("whaticket-active-note-id"));
        const found = list.find((n) => n.id === savedActiveId) || list[0];
        setActiveNoteId(found.id);
        setTitle(found.title || "Bloco de Notas");
        setNoteText(found.note || "");
        localStorage.setItem("whaticket-active-note-id", found.id);
      }
    } catch (err) {
      console.error("Erro ao buscar bloco de notas:", err);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  // Handle Note Switching
  const handleSelectNote = (selectedNote) => {
    setActiveNoteId(selectedNote.id);
    setTitle(selectedNote.title || "Bloco de Notas");
    setNoteText(selectedNote.note || "");
    localStorage.setItem("whaticket-active-note-id", selectedNote.id);
    setAnchorEl(null);
  };

  // Create a new Note
  const handleCreateNote = async () => {
    try {
      setSavingStatus("Criando...");
      const response = await api.post("/user-notes", {
        title: `Anotação ${notes.length + 1}`,
        note: ""
      });
      const newNote = response.data;
      setNotes((prev) => [newNote, ...prev]);
      setActiveNoteId(newNote.id);
      setTitle(newNote.title);
      setNoteText("");
      localStorage.setItem("whaticket-active-note-id", newNote.id);
      setSavingStatus("Salvo");
      setAnchorEl(null);
    } catch (err) {
      console.error("Erro ao criar novo bloco de notas:", err);
      setSavingStatus("Erro ao criar");
    }
  };

  // Auto-Save Effect (Debounced 1s)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!activeNoteId) return;

    setSavingStatus("Salvando...");
    const timeoutId = setTimeout(() => {
      api
        .put(`/user-notes/${activeNoteId}`, { title, note: noteText })
        .then((res) => {
          setSavingStatus("Salvo no banco de dados");
          setNotes((prevNotes) =>
            prevNotes.map((n) => (n.id === activeNoteId ? res.data : n))
          );
        })
        .catch((err) => {
          console.error("Erro ao salvar bloco de notas:", err);
          setSavingStatus("Erro ao salvar na nuvem");
        });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [noteText, title, activeNoteId]);

  // Open Delete Confirmation Dialog
  const handleOpenDeleteConfirm = (e, note) => {
    if (e) e.stopPropagation();
    setNoteToDelete(note);
    setDeleteConfirmOpen(true);
  };

  // Delete Note
  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await api.delete(`/user-notes/${noteToDelete.id}`);
      const updatedList = notes.filter((n) => n.id !== noteToDelete.id);
      setNotes(updatedList);
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);

      if (updatedList.length > 0) {
        if (noteToDelete.id === activeNoteId) {
          handleSelectNote(updatedList[0]);
        }
      } else {
        // Se deletou todas as notas, cria automaticamente uma nova nota limpa
        handleCreateNote();
      }
    } catch (err) {
      console.error("Erro ao deletar bloco de notas:", err);
    }
  };

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header Controls */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={1}
      >
        <Box display="flex" alignItems="center" style={{ flexGrow: 1, marginRight: 8 }}>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nome do Bloco"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              style: {
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#333",
                padding: "2px 4px",
                borderRadius: 4,
                backgroundColor: "rgba(0, 0, 0, 0.03)"
              }
            }}
            fullWidth
          />
        </Box>

        <Box display="flex" alignItems="center">
          <Tooltip title="Lista de Blocos">
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              style={{ padding: 4, marginRight: 2 }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Novo Bloco">
            <IconButton
              size="small"
              color="primary"
              onClick={handleCreateNote}
              style={{ padding: 4, marginRight: 2 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Excluir Bloco Atual">
            <IconButton
              size="small"
              style={{ padding: 4, color: "#d32f2f" }}
              onClick={(e) =>
                handleOpenDeleteConfirm(
                  e,
                  notes.find((n) => n.id === activeNoteId) || { id: activeNoteId, title }
                )
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Saving status indicator */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={0.5}>
        <Typography variant="caption" style={{ color: "#888", fontSize: "0.72rem" }}>
          {notes.length} bloco(s) de notas
        </Typography>
        <Typography
          variant="caption"
          style={{
            fontSize: "0.7rem",
            color: savingStatus.includes("Erro")
              ? "#d32f2f"
              : savingStatus.includes("Salvando") || savingStatus.includes("Criando")
              ? "#ed6c02"
              : "#2e7d32",
            fontWeight: 600
          }}
        >
          {savingStatus}
        </Typography>
      </Box>

      {/* Main Text Area */}
      <TextField
        multiline
        variant="outlined"
        placeholder="Escreva suas anotações aqui..."
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        rows={10}
        fullWidth
        style={{
          backgroundColor: "#fffde7",
          flexGrow: 1,
          borderRadius: 8
        }}
        InputProps={{
          style: {
            fontFamily: "Roboto, sans-serif",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            padding: 10
          }
        }}
      />

      {/* Menu / Dropdown for Notepad List */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: { maxHeight: 300, width: 260 }
        }}
      >
        <Box padding="6px 16px" display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Meus Blocos de Notas
          </Typography>
          <IconButton size="small" color="primary" onClick={handleCreateNote}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {notes.map((n) => (
          <MenuItem
            key={n.id}
            selected={n.id === activeNoteId}
            onClick={() => handleSelectNote(n)}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box display="flex" alignItems="center" overflow="hidden" style={{ flexGrow: 1 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <NoteIcon fontSize="small" color={n.id === activeNoteId ? "primary" : "action"} />
              </ListItemIcon>
              <ListItemText
                primary={n.title || "Sem título"}
                primaryTypographyProps={{
                  noWrap: true,
                  style: {
                    fontSize: "0.85rem",
                    fontWeight: n.id === activeNoteId ? 600 : 400
                  }
                }}
              />
            </Box>

            <IconButton
              size="small"
              onClick={(e) => handleOpenDeleteConfirm(e, n)}
              style={{ padding: 4, marginLeft: 4 }}
            >
              <DeleteIcon fontSize="small" style={{ fontSize: "1.1rem", color: "#888" }} />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Excluir Bloco de Notas?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir o bloco de notas "
            <strong>{noteToDelete?.title || "Anotação"}</strong>"? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="default">
            Cancelar
          </Button>
          <Button onClick={handleDeleteNote} color="secondary" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotepadWidget;
